import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, AlertTriangle, Zap } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchDossier, updateStatut, STATUTS } from "@/lib/dossiers";
import StatutSelector from "@/components/dossiers/StatutSelector";
import { ClientCard, EntrepriseCard, ActiviteCard } from "@/components/dossiers/DetailCards";
import DataroomSection from "@/components/dataroom/DataroomSection";
import LivrablesSection from "@/components/dataroom/LivrablesSection";
import SyntheseIaSection from "@/components/ia/SyntheseIaSection";
import RapportDoubleSection from "@/components/ia/RapportDoubleSection";
import ChecklistCard from "@/components/dossiers/ChecklistCard";
import AnalyseDirigeantTab from "@/components/pappers/AnalyseDirigeantTab";
import RisqueBadge from "@/components/dossiers/RisqueBadge";
import KpiSection from "@/components/kpis/KpiSection";
import QuestionnaireDirigeant from "@/components/questionnaire/QuestionnaireDirigeant";
import PartageClientCard from "@/components/dossiers/PartageClientCard";
import AltaresBadge from "@/components/dossiers/AltaresBadge";
import ContexteMaster from "@/components/dossiers/ContexteMaster";
import { isRestructuration, labelContexte } from "@/lib/checklist";
import { isImmatriculation } from "@/lib/dossiers";
import { FolderOpen, Building2 } from "lucide-react";
import IntraGroupeCard from "@/components/dossiers/IntraGroupeCard";
import ContexteEditor from "@/components/dossiers/ContexteEditor";
import MemoFlashSection from "@/components/memo/MemoFlashSection";
import PremierRdvAjSection from "@/components/memo/PremierRdvAjSection";
import AudienceOuvertureSection from "@/components/memo/AudienceOuvertureSection";
import PeriodeObservationSection from "@/components/memo/PeriodeObservationSection";
import ArretePlanSection from "@/components/memo/ArretePlanSection";
import PrevisionnelsTab from "@/components/memo/PrevisionnelsTab";
import BusinessPlanTab from "@/components/memo/BusinessPlanTab";
import RapportsMensuelsTab from "@/components/memo/RapportsMensuelsTab";
import CashRjTab from "@/components/memo/CashRjTab";
import InvestigationChat from "@/components/ia/InvestigationChat";
import NotesConfidentielles from "@/components/notes/NotesConfidentielles";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import LiveAssistant from "@/components/memo/LiveAssistant";
import ForensicSection from "@/components/forensic/ForensicSection";
import { cn } from "@/lib/utils";

// SUPER-LOT 126 — Ordre strict de la navigation + étanchéité In Bonis.
// Les onglets `restructurationOnly` sont masqués hors Restructuration / Procédure collective.
const ALL_TABS = [
    { id: "dossier", label: "Dossier & Dataroom" },
    { id: "questionnaire", label: "Questionnaire Dirigeant" },
    { id: "analyse", label: "Analyse du Dirigeant" },
    { id: "kpis", label: "KPIs Financiers" },
    { id: "investigation", label: "Investigation" },
    { id: "notes", label: "Notes Confidentielles", restructurationOnly: true },
    { id: "rapports", label: "Rapports de Procédure", restructurationOnly: true },
    { id: "previsionnel", label: "Prévisionnels" },
    { id: "cash-rj", label: "CASH RJ", restructurationOnly: true },
    { id: "rapports-mensuels", label: "Rapports mensuels", restructurationOnly: true },
    { id: "business-plan", label: "Business Plan" },
];

function buildTabs(contexteAudit) {
    const resto = isRestructuration(contexteAudit);
    return ALL_TABS.filter((t) => resto || !t.restructurationOnly);
}

export default function DossierDetail() {
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [dossier, setDossier] = useState(null);
    const tabs = buildTabs(dossier?.contexte_audit);
    const tab = tabs.some((t) => t.id === searchParams.get("tab")) ? searchParams.get("tab") : "dossier";
    const setTab = (t) => setSearchParams({ tab: t }, { replace: true });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [docCount, setDocCount] = useState(null);
    const [docs, setDocs] = useState(null);
    const [kpiCount, setKpiCount] = useState(null);
    const [hasSynthese, setHasSynthese] = useState(false);
    const [validating, setValidating] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        fetchDossier(id)
            .then((d) => { if (!cancelled) (d ? setDossier(d) : setError("Ce dossier n'existe pas ou a été supprimé.")); })
            .catch((e) => { if (!cancelled) setError(e.message); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [id]);

    async function changeStatut(statut) {
        const prev = dossier.statut_dossier;
        setDossier((d) => ({ ...d, statut_dossier: statut }));
        try {
            await updateStatut(id, statut);
            const fresh = await fetchDossier(id);
            if (fresh) setDossier(fresh);
            toast.success("Statut mis à jour", { description: STATUTS[statut] });
        } catch (e) {
            setDossier((d) => ({ ...d, statut_dossier: prev }));
            toast.error("Échec de la mise à jour", { description: e.message });
        }
    }

    const back = (
        <Button asChild variant="ghost" data-testid="btn-back-dashboard" className="rounded-full h-9 px-4 -ml-4 text-muted-foreground">
            <Link to="/dashboard">
                <ArrowLeft className="h-4 w-4" strokeWidth={2} />
                Tableau de bord
            </Link>
        </Button>
    );

    if (loading) {
        return (
            <div data-testid="dossier-loading" className="space-y-6">
                {back}
                <Skeleton className="h-16 w-2/3" />
                <Skeleton className="h-40 w-full" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-6">
                {back}
                <div data-testid="dossier-error" className="flex gap-3 text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-4 py-4">
                    <AlertTriangle className="h-5 w-5 shrink-0" strokeWidth={1.75} />
                    {error}
                </div>
            </div>
        );
    }

    const created = new Date(dossier.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
    const contexteCourt = labelContexte(dossier.contexte_audit).split(/\s*[(&]/)[0].trim();

    return (
        <div data-testid="dossier-detail-page" className="space-y-10">
            <header className="border-b border-border pb-8 space-y-6">
                {back}
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <p data-testid="dossier-type" className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
                            Mission · ouverte le {created}
                        </p>
                        <h1 data-testid="dossier-title" className="mt-3 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-foreground">
                            {dossier.entreprises?.nom_entreprise || dossier.nom_mission}
                            <span className="text-2xl sm:text-3xl text-muted-foreground font-normal"> — {contexteCourt} — {created}</span>
                        </h1>
                        <p data-testid="dossier-entreprise" className="mt-3 text-sm text-muted-foreground">
                            <span className="text-foreground font-medium">{dossier.nom_mission}</span>
                            {isImmatriculation(dossier.entreprises?.siren)
                                ? <span className="italic"> · En cours d&apos;immatriculation</span>
                                : <span className="font-mono tracking-wider"> · SIREN {dossier.entreprises?.siren}</span>}
                            {dossier.users_clients?.prenom_nom && <span> · Contact : {dossier.users_clients.prenom_nom}</span>}
                        </p>
                    </div>
                    <div className="flex flex-col items-end gap-3">
                        <AltaresBadge siren={dossier.entreprises?.siren} />
                        <RisqueBadge
                            dossierId={id}
                            size="lg"
                            value={dossier.niveau_risque}
                            source={dossier.niveau_risque_source}
                            onChange={(n, s) => setDossier((d) => ({ ...d, niveau_risque: n, niveau_risque_source: s }))}
                        />
                        <StatutSelector value={dossier.statut_dossier} onChange={changeStatut} />
                        {dossier.statut_dossier !== "pret_pour_analyse" && dossier.statut_dossier !== "clos" && (
                            <Button
                                variant="outline"
                                size="sm"
                                data-testid="forcer-analyse-button"
                                onClick={() => changeStatut("pret_pour_analyse")}
                                className="rounded-full border-amber-500/50 text-amber-700 hover:bg-amber-50"
                            >
                                <Zap className="h-4 w-4" strokeWidth={1.75} /> Forcer l&apos;analyse (Pièces suffisantes)
                            </Button>
                        )}
                    </div>
                </div>
            </header>

            <nav data-testid="dossier-tabs" className="flex gap-1 border-b-2 border-slate-200 overflow-x-auto">
                {tabs.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        data-testid={`tab-${t.id}`}
                        aria-selected={tab === t.id}
                        onClick={() => setTab(t.id)}
                        className={cn(
                            "px-4 py-2.5 text-sm font-medium -mb-0.5 border-b-[3px] rounded-t-md transition-colors whitespace-nowrap",
                            tab === t.id ? "border-indigo-600 bg-indigo-50/70 text-indigo-700 font-semibold" : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-100/70"
                        )}
                    >
                        {t.label}
                    </button>
                ))}
            </nav>

            <div data-testid="tab-panel-dossier" className={cn("space-y-10", tab !== "dossier" && "hidden")}>
                <section className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    <EntrepriseCard entreprise={dossier.entreprises} />
                    <ClientCard client={dossier.users_clients} />
                    <ActiviteCard
                        documents={docCount ?? dossier.documents_dataroom?.length ?? 0}
                        kpis={kpiCount ?? dossier.kpis_financiers?.length ?? 0}
                        synthese={hasSynthese}
                        exercices={dossier.exercices_fiscaux ?? []}
                        notes={dossier.notes}
                    />
                </section>
                <ContexteMaster dossierId={id} contexte={dossier.contexte_audit} contexteAutre={dossier.contexte_autre} onChange={(c, a) => setDossier((d) => ({ ...d, contexte_audit: c, contexte_autre: a }))} />
                <PartageClientCard dossierId={id} showPrevisionnel={true} />
                <section data-testid="section-entite" className="space-y-6">
                    <h2 className="font-display text-2xl text-foreground flex items-center gap-2"><FolderOpen className="h-5 w-5 text-primary" strokeWidth={1.75} /> Entité auditée</h2>
                    <IntraGroupeCard dossierId={id} siren={dossier.entreprises?.siren} />
                    <ChecklistCard
                        dossierId={id}
                        contexteAudit={dossier.contexte_audit}
                        contexteAutre={dossier.contexte_autre}
                        onContexteChange={(c, a) => setDossier((d) => ({ ...d, contexte_audit: c, contexte_autre: a }))}
                        typeDossier={dossier.type_dossier}
                        documents={(docs ?? []).filter((d) => !d.est_holding)}
                        documentsRequis={dossier.documents_requis ?? undefined}
                        onDocumentsRequisChange={(v) => setDossier((d) => ({ ...d, documents_requis: v }))}
                        statut={dossier.statut_dossier}
                        validating={validating}
                        onValidate={async () => { setValidating(true); try { await changeStatut("pret_pour_analyse"); } finally { setValidating(false); } }}
                        relance={{ dossierId: id, clientEmail: dossier.users_clients?.email, client: dossier.users_clients?.prenom_nom, entreprise: dossier.entreprises?.nom_entreprise, mission: dossier.nom_mission }}
                    />
                    {isRestructuration(dossier.contexte_audit) && <LivrablesSection mode="admin" dossierId={id} isResto />}
                    <DataroomSection dossierId={id} group="entite" contexteAudit={dossier.contexte_audit} onCountChange={setDocCount} onDocsChange={setDocs} />
                </section>
                <section data-testid="section-holding" className="space-y-6">
                    <h2 className="font-display text-2xl text-foreground flex items-center gap-2"><Building2 className="h-5 w-5 text-primary" strokeWidth={1.75} /> Holding / Groupe</h2>
                                            <ChecklistCard holding contexteAudit={dossier.contexte_audit} typeDossier={dossier.type_dossier} documents={(docs ?? []).filter((d) => d.est_holding)} />
                    <DataroomSection dossierId={id} group="holding" contexteAudit={dossier.contexte_audit} />
                </section>
            </div>

            <div data-testid="tab-panel-kpis" className={cn("space-y-6", tab !== "kpis" && "hidden")}>
                <KpiSection dossierId={id} onCountChange={setKpiCount} />
            </div>

            <div data-testid="tab-panel-questionnaire" className={cn("space-y-6", tab !== "questionnaire" && "hidden")}>
                <QuestionnaireDirigeant dossierId={id} contexteAudit={dossier.contexte_audit} />
            </div>

            <div data-testid="tab-panel-previsionnel" className={cn(tab !== "previsionnel" && "hidden")}>
                <PrevisionnelsTab dossierId={id} contexteAudit={dossier.contexte_audit} />
            </div>

            {isRestructuration(dossier.contexte_audit) && (
                <div data-testid="tab-panel-rapports-mensuels" className={cn(tab !== "rapports-mensuels" && "hidden")}>
                    <RapportsMensuelsTab dossierId={id} />
                </div>
            )}

            <div data-testid="tab-panel-business-plan" className={cn(tab !== "business-plan" && "hidden")}>
                <BusinessPlanTab dossierId={id} contexteAudit={dossier.contexte_audit} />
            </div>

            {isRestructuration(dossier.contexte_audit) && (
                <div data-testid="tab-panel-cash-rj" className={cn(tab !== "cash-rj" && "hidden")}>
                    <CashRjTab dossierId={id} />
                </div>
            )}

            <div data-testid="tab-panel-investigation" className={cn(tab !== "investigation" && "hidden")}>
                <Accordion type="multiple" defaultValue={["forensic"]} className="space-y-4">
                    <AccordionItem value="forensic" data-testid="investigation-accordion-forensic" className="rounded-xl border border-slate-200 bg-white shadow-sm px-5">
                        <AccordionTrigger className="py-4 hover:no-underline"><span className="text-base font-semibold text-slate-900">Moteur Forensic (Scan FEC)</span></AccordionTrigger>
                        <AccordionContent className="pb-6"><ForensicSection dossierId={id} /></AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="chat" data-testid="investigation-accordion-chat" className="rounded-xl border border-slate-200 bg-white shadow-sm px-5">
                        <AccordionTrigger className="py-4 hover:no-underline"><span className="text-base font-semibold text-slate-900">Chat d&apos;Investigation</span></AccordionTrigger>
                        <AccordionContent className="pb-6"><InvestigationChat dossierId={id} /></AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="memo" data-testid="investigation-accordion-memo" className="rounded-xl border border-slate-200 bg-white shadow-sm px-5">
                        <AccordionTrigger className="py-4 hover:no-underline"><span className="text-base font-semibold text-slate-900">Mémo Flash</span></AccordionTrigger>
                        <AccordionContent className="pb-6"><MemoFlashSection dossierId={id} /></AccordionContent>
                    </AccordionItem>
                </Accordion>
            </div>
            <LiveAssistant dossierId={id} />

            <div data-testid="tab-panel-rapports" className={cn("space-y-6", tab !== "rapports" && "hidden")}>
                {isRestructuration(dossier.contexte_audit) ? (
                    <Accordion type="multiple" defaultValue={[]} className="space-y-4">
                        <AccordionItem value="rdvaj" data-testid="rapport-accordion-rdvaj" className="rounded-xl border border-slate-200 bg-white shadow-sm px-5">
                            <AccordionTrigger className="py-4 hover:no-underline"><span className="text-base font-semibold text-slate-900">1 - Premier RDV AJ</span></AccordionTrigger>
                            <AccordionContent className="pb-6"><PremierRdvAjSection dossierId={id} /></AccordionContent>
                        </AccordionItem>
                        <AccordionItem value="audience" data-testid="rapport-accordion-audience" className="rounded-xl border border-slate-200 bg-white shadow-sm px-5">
                            <AccordionTrigger className="py-4 hover:no-underline"><span className="text-base font-semibold text-slate-900">2 - Audience d&apos;Ouverture (Préparation Formulaire DCP)</span></AccordionTrigger>
                            <AccordionContent className="pb-6"><AudienceOuvertureSection dossierId={id} /></AccordionContent>
                        </AccordionItem>
                        <AccordionItem value="periode" data-testid="rapport-accordion-periode" className="rounded-xl border border-slate-200 bg-white shadow-sm px-5">
                            <AccordionTrigger className="py-4 hover:no-underline"><span className="text-base font-semibold text-slate-900">3 - Audiences de Période d&apos;Observation</span></AccordionTrigger>
                            <AccordionContent className="pb-6"><PeriodeObservationSection dossierId={id} /></AccordionContent>
                        </AccordionItem>
                        <AccordionItem value="arrete" data-testid="rapport-accordion-arrete" className="rounded-xl border border-slate-200 bg-white shadow-sm px-5">
                            <AccordionTrigger className="py-4 hover:no-underline"><span className="text-base font-semibold text-slate-900">4 - Arrêté du Plan (10 ans)</span></AccordionTrigger>
                            <AccordionContent className="pb-6"><ArretePlanSection dossierId={id} /></AccordionContent>
                        </AccordionItem>
                    </Accordion>
                ) : (
                    <div className="rounded-md border border-dashed border-border px-6 py-10 text-center text-sm text-muted-foreground">Les rapports de procédure sont disponibles pour les dossiers en Procédures Collectives (contexte Restructuration).</div>
                )}
            </div>

            {isRestructuration(dossier.contexte_audit) && (
                <div data-testid="tab-panel-notes" className={cn("space-y-10", tab !== "notes" && "hidden")}>
                    <RapportDoubleSection dossierId={id} />
                    <NotesConfidentielles dossierId={id} />
                </div>
            )}

            <div data-testid="tab-panel-analyse" className={cn(tab !== "analyse" && "hidden")}>
                <AnalyseDirigeantTab dossierId={id} siren={dossier.entreprises?.siren} nomEntreprise={dossier.entreprises?.nom_entreprise} />
            </div>
        </div>
    );
}
